import { ComposeOption, DatasetComponentOption } from 'echarts';
import { BarSeriesOption, BarChart as EChartsBarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  GridComponentOption,
  TitleComponent,
  TooltipComponent,
  TooltipComponentOption,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { ContentDimensions, formatValue, GaugeChartThresholdOptions, UnitOptions, useFindThreshold } from '@/core';
import { useChartsTheme } from '../context';
import { EChart } from '../EChart';
import { NoDataOption } from '../model';

type BarChartOption = ComposeOption<
  BarSeriesOption | TooltipComponentOption | GridComponentOption | DatasetComponentOption
>;

// eslint-disable-next-line react-hooks/rules-of-hooks
use([EChartsBarChart, CanvasRenderer, DatasetComponent, GridComponent, TitleComponent, TooltipComponent]);

interface BarGaugeData {
  label: string;
  value: number | null;
}

interface BarGaugeProps extends ContentDimensions {
  max: number;
  data: BarGaugeData[];
  thresholds?: GaugeChartThresholdOptions;
  unit?: UnitOptions;
}

type BarGaugeDatasetSource = {
  label: string;
  value: number;
};

const DEFAULT_THRESHOLDS: GaugeChartThresholdOptions = {};

const BAR_MIN_WIDTH = 14;

const BAR_GAP = 6;

const LABEL_STYLE = {
  show: true,
  position: 'right',
  barMinWidth: BAR_MIN_WIDTH,
  barCategoryGap: BAR_GAP,
};

const REMOVE_NEW_LINES_REGEX = /[\r\n]+/g;

export const BarGaugeBase = ({ data, width, height, thresholds: thresholdsProp, max, unit }: BarGaugeProps) => {
  const chartsTheme = useChartsTheme();

  const thresholds = thresholdsProp ?? DEFAULT_THRESHOLDS;

  const findThreshold = useFindThreshold({ thresholds, palette: chartsTheme.thresholds, max });

  const itemStyle = useMemo(() => {
    return {
      color: ({ value: { value } }: { value: BarGaugeDatasetSource }) => {
        const threshold = findThreshold(value || 0);

        return threshold.color;
      },
    };
  }, [findThreshold]);

  const { dataset, values, labels } = useMemo(() => {
    const values: Array<BarGaugeData['value']> = [];
    const labels: Array<BarGaugeData['label']> = [];
    const datasetValues: BarGaugeDatasetSource[] = [];
    for (const datum of data) {
      const value = datum.value;
      const label = datum.label.replace(REMOVE_NEW_LINES_REGEX, ''); // strip out any newline characters to ensure labels do not overlap

      datasetValues.push({ label, value: value ?? 0 });
      values.push(value);
      labels.push(label);
    }

    const dataset = {
      source: datasetValues,
      dimensions: ['label', 'value'],
    };

    return { dataset, values, labels };
  }, [data]);

  const xAxis = useMemo(
    () => ({
      max,
      show: false,
      axisLabel: {
        hideOverlap: true,
      },
    }),
    [max]
  );

  const yAxis: BarChartOption['yAxis'] = useMemo(
    () => [
      {
        axisLine: {
          show: false,
        },
        type: 'category',
        splitLine: {
          show: false,
        },
        axisLabel: {
          overflow: 'truncate',
          width: width / 3,
          color: chartsTheme.theme.palette.text.legacySecondary,
        },
        data: labels,
        inverse: true,
      },
      {
        axisLine: {
          show: false,
        },
        type: 'category',
        splitLine: {
          show: false,
        },
        axisLabel: {
          overflow: 'none',
        },
        data: values.map((value) => ({
          value: value !== null ? formatValue(value, unit) : `${value}`,
          textStyle: {
            color: findThreshold(value || 0).color,
          },
        })),
        inverse: true,
      },
    ],
    [chartsTheme, findThreshold, labels, values, unit, width]
  );

  const series = useMemo(
    () => [
      {
        type: 'bar',
        showBackground: true,
        backgroundStyle: { color: chartsTheme.theme.palette.background.canvas },
        label: {
          show: false,
        },
        itemStyle: itemStyle,
        labelStyle: LABEL_STYLE,
      },
    ],
    [chartsTheme, itemStyle]
  );

  const tooltip = useMemo(
    () => ({
      appendToBody: true,
      confine: true,
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: Array<{ value: BarGaugeDatasetSource }>) => {
        const source = params[0]?.value;

        if (source !== undefined) {
          const threshold = findThreshold(source.value);

          if (threshold.name) {
            return threshold.name;
          }
        }
      },
      backgroundColor: chartsTheme.theme.palette.background.popover,
      textStyle: {
        color: chartsTheme.theme.palette.text.primary,
        fontSize: chartsTheme.theme.typography.fontSize,
      },
      borderWidth: 0,
      extraCssText: `boxShadow: ${chartsTheme.theme.shadows[5]}`,
    }),
    [chartsTheme, findThreshold]
  );

  const option: BarChartOption | NoDataOption = useMemo(() => {
    if (data == null || !data.length) return chartsTheme.noDataOption;

    return {
      animation: false,
      title: {
        show: false,
      },
      xAxis,
      yAxis,
      dataset,
      series,
      tooltip,
      grid: {
        // Matching top from default theme in our theme-gen.
        top: 5,
        bottom: 5,
        left: 16,
        right: 16,
      },
    };
  }, [data, xAxis, yAxis, dataset, series, tooltip, chartsTheme]);

  return (
    <div style={{ width, height, overflow: 'auto' }}>
      <EChart
        width={width}
        height={data ? Math.max(data.length * (BAR_MIN_WIDTH + BAR_GAP), height) : height}
        option={option}
        theme={chartsTheme.echartsTheme}
      />
    </div>
  );
};
